import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "flowbite-react";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { useFormikWrapper } from "../../components/formik_wrapper";
import { TextBox } from "../../components/inputs/index";
import { axiosGet, axiosPost } from "../../util/axios";

const REDIRECT_DELAY_MS = 4000;
// The partner-facing ticket hub. This admin console is NOT the hub, so every
// post-setup redirect and link on this page points here rather than /login.
const PARTNER_HUB_URL = "https://partner-ticket-hub.zuper.co/";

function Onboarding() {
  const { token } = useParams();

  const [status, setStatus] = useState("loading"); // loading | ready | invalid | complete
  const [context, setContext] = useState(null);

// coworker link validation check
  useEffect(() => {
    axiosGet(`/coworkers/reset-partner-password/${token}`).then((res) => {
      if (res?.status === 200 && res?.data?.data) {
        setContext(res.data.data);
        setStatus("ready");
      } else {
        setStatus("invalid");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "complete") {
      const timer = setTimeout(
        () => window.location.replace(PARTNER_HUB_URL),
        REDIRECT_DELAY_MS
      );
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const setupValidation = Yup.object({
    two_fa_code: Yup.string()
      .required("2FA code is required")
      .length(6, "Please enter a valid 6-digit code"),
    new_password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters"),
    confirm_password: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("new_password")], "Passwords do not match"),
  });

  const setupForm = useFormikWrapper({
    initialValues: {
      two_fa_code: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: setupValidation,
    onSubmit: async (values) => {
      const res = await axiosPost(`/coworkers/reset-partner-password/${token}`, {
        two_fa_code: values.two_fa_code,
        new_password: values.new_password,
      });
      setupForm.setSubmitting(false);
      if (res?.status === 200) {
        setStatus("complete");
      } else if (res?.status === 410) {
        setStatus("invalid");
      } else {
        toast.error(res?.data?.message || "Unable to complete setup. Please try again.");
      }
    },
  });

  return (
    <div className="flex flex-col bg-white h-screen">
      <div className="flex min-h-full flex-1">
        <div className="relative hidden w-0 flex-1 lg:block">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1908&q=80"
            alt=""
          />
        </div>
        <div className="flex flex-1 flex-col justify-center px-4 py-6 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <img
              className="h-10 w-auto"
              src="https://assets-global.website-files.com/62b4a93516caf13604744768/62cfcbb0937956380eea27cd_zuper-logo.svg"
              alt="Zuper"
            />

            {status === "loading" && (
              <p className="mt-8 text-sm text-gray-500">Loading...</p>
            )}

            {status === "invalid" && (
              <>
                <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                  This link has expired
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  This setup link has expired or has already been used. Please reach out to your Zuper
                  administrator for a new link.
                </p>
              </>
            )}

            {status === "complete" && (
              <>
                <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                  You're all set
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  Your password has been set successfully. Taking you to the Partner
                  Support Hub...
                </p>
                <p className="mt-4 text-sm text-gray-500">
                  <a
                    className="cursor-pointer text-indigo-600 underline"
                    href={PARTNER_HUB_URL}
                  >
                    Click here
                  </a>{" "}
                  if you are not redirected automatically.
                </p>
              </>
            )}

            {status === "ready" && (
              <>
                <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                  1.Complete your account setup
                </h2>
                <p className="mt-2 text-sm text-gray-500">{context?.email}</p>

                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-900">
                    1. Scan this QR code with Google Authenticator
                  </p>
                  {context?.qr && (
                    <img className="mt-3 h-40 w-40" src={context.qr} alt="2FA QR Code" />
                  )}
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                  className="mt-6 space-y-4"
                >
                  <TextBox
                    name="two_fa_code"
                    label="2. Enter the 6-digit code"
                    placeholder="123456"
                    formik={setupForm}
                  />

                  <TextBox
                    name="new_password"
                    label="3. Choose a new password"
                    placeholder="************"
                    type="password"
                    formik={setupForm}
                  />

                  <TextBox
                    name="confirm_password"
                    label="Confirm password"
                    placeholder="************"
                    type="password"
                    formik={setupForm}
                  />

                  <div>
                    <Button
                      type="submit"
                      disabled={setupForm.isSubmitting}
                      onClick={setupForm.handleSubmit}
                      className="mt-2 w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      Complete setup
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
